import { ChangeEvent, MouseEvent, Ref, useState } from "react";
import { t } from "@lingui/core/macro";
import { Temporal } from "temporal-polyfill";

import { NON_BREAKING_DASH } from "_/utils/consts";
import {
  type DisplayTimeZone,
  timeZoneId,
  toPlainDateTime,
} from "_/utils/datetime";

import * as S from "./styled";

// `datetime-local` inputs hold wall-clock time to the minute without a time
// zone, e.g. "2026-10-08T09:00", so they're shown and read in the display
// time zone.
function toInputValue(
  epochMs: number,
  displayTimeZone: DisplayTimeZone
): string {
  const plainDateTime = toPlainDateTime(epochMs, displayTimeZone);
  return plainDateTime?.toString({ smallestUnit: "minute" }) ?? "";
}

function fromInputValue(value: string): Temporal.PlainDateTime | null {
  try {
    return Temporal.PlainDateTime.from(value);
  } catch {
    return null;
  }
}

// Typing a year into the input passes through values like 0002 and 0202 on
// the way to 2026, which shouldn't be queried.
const MIN_YEAR = 1000;

/** Focus a date/time input and open the browser's picker for it. */
export function openPicker(input: HTMLInputElement) {
  input.focus();
  try {
    input.showPicker();
  } catch {
    // The picker can't always be opened (e.g. one is already showing), but
    // the input can still be edited by typing.
  }
}

type DateTimeInputProps = {
  value: string;
  min?: string;
  max?: string;
  label: string;
  displayTimeZone: DisplayTimeZone;
  inputRef?: Ref<HTMLInputElement>;
  onCommit: (instant: Temporal.Instant) => void;
};

/**
 * A `datetime-local` input that opens the browser's date/time picker when
 * clicked, and commits only complete, plausible values.
 */
const DateTimeInput = ({
  value,
  min,
  max,
  label,
  displayTimeZone,
  inputRef,
  onCommit,
}: DateTimeInputProps) => {
  // Keep a draft so partially edited values aren't reset by React while the
  // user types. When the committed value changes from elsewhere (e.g. the
  // range dropdown), replace the draft with it.
  const [draft, setDraft] = useState(value);
  const [committedValue, setCommittedValue] = useState(value);
  if (value !== committedValue) {
    setCommittedValue(value);
    setDraft(value);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    setDraft(event.target.value);
    const dateTime = fromInputValue(event.target.value);
    if (dateTime && dateTime.year >= MIN_YEAR) {
      const zoned = dateTime.toZonedDateTime(timeZoneId(displayTimeZone));
      onCommit(zoned.toInstant());
    }
  }

  return (
    <S.RangeInput
      ref={inputRef}
      type="datetime-local"
      value={draft}
      min={min}
      max={max}
      aria-label={label}
      title={label}
      onClick={(event: MouseEvent<HTMLInputElement>) =>
        openPicker(event.currentTarget)
      }
      onChange={handleChange}
    />
  );
};

type RangeInputsProps = {
  startMs: number;
  endMs: number;
  displayTimeZone: DisplayTimeZone;
  /** Lets the parent focus the start input, e.g. when "Custom" is chosen. */
  startInputRef?: Ref<HTMLInputElement>;
  onRangeChange: (start: Temporal.Instant, end: Temporal.Instant) => void;
  onDisplayTimeZoneChange: (displayTimeZone: DisplayTimeZone) => void;
};

/**
 * Editable start and end of the queried time range, plus a toggle for
 * showing times in UTC. Editing either end replaces the range with an
 * explicit one.
 */
export const RangeInputs = ({
  startMs,
  endMs,
  displayTimeZone,
  startInputRef,
  onRangeChange,
  onDisplayTimeZoneChange,
}: RangeInputsProps) => {
  const start = toInputValue(startMs, displayTimeZone);
  const end = toInputValue(endMs, displayTimeZone);

  const commitStart = (newStart: Temporal.Instant) =>
    onRangeChange(newStart, Temporal.Instant.fromEpochMilliseconds(endMs));
  const commitEnd = (newEnd: Temporal.Instant) =>
    onRangeChange(Temporal.Instant.fromEpochMilliseconds(startMs), newEnd);

  return (
    <S.QueryRange>
      <DateTimeInput
        value={start}
        max={end}
        label={t`components.machine-logs.range-start`}
        displayTimeZone={displayTimeZone}
        inputRef={startInputRef}
        onCommit={commitStart}
      />
      {NON_BREAKING_DASH}
      <DateTimeInput
        value={end}
        min={start}
        label={t`components.machine-logs.range-end`}
        displayTimeZone={displayTimeZone}
        onCommit={commitEnd}
      />
      <S.UtcCheckbox
        label={t`components.machine-logs.utc`}
        checked={displayTimeZone === "utc"}
        onChange={(checked) =>
          onDisplayTimeZoneChange(checked ? "utc" : "local")
        }
      />
    </S.QueryRange>
  );
};
